import type { CSSProperties, FC, ReactNode } from 'react';
import {
  BarChartToolIcon,
  BuildingOfficeIcon,
  CalendarScheduleIcon,
  FileDocIcon,
  FolderArchiveDocIcon,
  GlobeEnuIcon,
  PlusSymbolIcon,
  ShareNodesIcon,
  UsersGroupIcon,
} from './DepartmentIcons';

interface DepartmentCabinetGridProps {
  roleTitle: string;
  onOpenWorkspace: (defaultView?: string) => void;
  onOpenSuperAi: () => void;
  onOpenRequestGateway: () => void;
  onOpenModal: (modalKey: string) => void;
}

type HubCard = {
  num: string;
  title: string;
  desc: string;
  accent: string;
  icon: ReactNode;
  onClick: () => void;
};

/** NEW-VERSION-1.83 R517 DEPARTMENT_HEAD — governance-oriented central set. */
export const DepartmentCabinetGrid: FC<DepartmentCabinetGridProps> = ({
  roleTitle,
  onOpenWorkspace,
  onOpenSuperAi,
  onOpenRequestGateway,
  onOpenModal,
}) => {
  const cards: HubCard[] = [
    {
      num: '01',
      title: 'Панель кафедры',
      desc: 'Состав кафедры, показатели, контрольные точки и административный контур.',
      accent: '#41d2ff',
      icon: <BuildingOfficeIcon className="w-5 h-5" />,
      onClick: () => onOpenWorkspace('overview'),
    },
    {
      num: '02',
      title: 'Мониторинг докторантов',
      desc: 'Реестр обучения и защит: годы, программы, руководители и статусы.',
      accent: '#49ddb7',
      icon: <UsersGroupIcon className="w-5 h-5" />,
      onClick: () => onOpenWorkspace('monitoring'),
    },
    {
      num: '03',
      title: 'Мои заявки',
      desc: 'Услуги, статьи, проекты, конференции, гранты и история статусов.',
      accent: '#9e80ff',
      icon: <FileDocIcon className="w-5 h-5" />,
      onClick: () => onOpenModal('my_requests'),
    },
    {
      num: '04',
      title: 'Академические связи',
      desc: 'Контакты, группы, совместные материалы и сообщения.',
      accent: '#5da8ff',
      icon: <ShareNodesIcon className="w-5 h-5" />,
      onClick: () => onOpenModal('academic_connections'),
    },
    {
      num: '05',
      title: 'Программы и инструменты',
      desc: 'Опросы, эксперименты и исследовательские сервисы академии.',
      accent: '#e2ad3a',
      icon: <BarChartToolIcon className="w-5 h-5" />,
      onClick: () => onOpenModal('research_tools'),
    },
    {
      num: '06',
      title: 'Встречи и дежурства',
      desc: 'Встречи, дежурства, приглашения и синхронизация сроков.',
      accent: '#ff7a64',
      icon: <CalendarScheduleIcon className="w-5 h-5" />,
      onClick: () => onOpenWorkspace('events'),
    },
    {
      num: '07',
      title: 'Файлы и документы',
      desc: 'Кафедральные файлы, версии, загрузка и контроль комплектности.',
      accent: '#66b7e8',
      icon: <FolderArchiveDocIcon className="w-5 h-5" />,
      onClick: () => onOpenModal('files_hub'),
    },
    {
      num: '08',
      title: 'Официальные источники ЕНУ',
      desc: 'Подтверждённые маршруты, регламенты и официальные источники.',
      accent: '#41d2ff',
      icon: <GlobeEnuIcon className="w-5 h-5" />,
      onClick: () => onOpenModal('enu_sources'),
    },
  ];

  return (
    <div className="w-full role-cabinet-page">
      <div className="w-full py-8 space-y-6">
        <div className="role-hero" style={{ '--role-accent': '#41d2ff' } as CSSProperties}>
          <div className="min-w-0">
            <small>{roleTitle}</small>
            <h2>Кабинет · {roleTitle}</h2>
            <p>Общий кафедральный контур: списки, поручения, опросы, документы, отчётность и встречи.</p>
          </div>
          <div className="role-hero-actions">
            <button type="button" className="role-btn" onClick={onOpenSuperAi}>
              Super AI
            </button>
            <button type="button" className="role-btn primary" onClick={onOpenRequestGateway}>
              <PlusSymbolIcon className="w-4 h-4 inline mr-1" />
              Создать заявку
            </button>
          </div>
        </div>

        <div className="owner-hub-top-actions">
          <button type="button" className="owner-hub-ai-action" onClick={onOpenSuperAi}>
            <span className="spark" aria-hidden="true">
              ✦
            </span>
            <span>
              <strong>Super AI</strong>
              <small>Помощник по функциям кафедрального кабинета</small>
            </span>
            <b>Открыть →</b>
          </button>
          <button type="button" className="owner-hub-create" onClick={onOpenRequestGateway}>
            + Создать заявку
          </button>
        </div>

        <section style={{ marginTop: 8 }} aria-label="Разделы руководителя кафедры">
          <div style={{ marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>Разделы руководителя</h3>
            <p style={{ margin: '4px 0 0', color: '#8faebb', fontSize: 12 }}>
              8 рабочих контуров управления подразделением
            </p>
          </div>
          <div className="owner-hub-grid">
            {cards.map((card) => (
              <button
                key={card.num}
                type="button"
                onClick={card.onClick}
                className="owner-hub-card"
                style={{ '--action': card.accent } as CSSProperties}
              >
                <span className="owner-hub-index">{card.num}</span>
                <span className="owner-hub-icon" aria-hidden="true">
                  {card.icon}
                </span>
                <span className="owner-hub-copy">
                  <strong>{card.title}</strong>
                  <span>{card.desc}</span>
                </span>
                <span className="owner-hub-arrow" aria-hidden="true">
                  ›
                </span>
              </button>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};
